import embeddedAppUtils from 'utils/embeddedAppUtils';
import storageService, { Locals } from 'utils/storageService';
import { isDebugModeEnabled } from './debug-mode';

type ConsoleFetcherOptions = {
    base?: string;
    headers?: Record<string, string>;
    raw?: boolean;
};

type ConsoleFetcher = {
    get: <T>(path: string, options?: ConsoleFetcherOptions) => Promise<T>;
    post: <T>(path: string, body?: object, options?: ConsoleFetcherOptions) => Promise<T>;
    put: <T>(path: string, body?: object, options?: ConsoleFetcherOptions) => Promise<T>;
    patch: <T>(path: string, body?: object, options?: ConsoleFetcherOptions) => Promise<T>;
    del: <T>(path: string, options?: ConsoleFetcherOptions) => Promise<T>;
    token: () => Promise<string>;
    setBase: (base: string) => string;
    readonly base: string;
    readonly go: string;
    readonly php: string;
};

declare global {
    interface Window {
        pb?: ConsoleFetcher;
    }
}

const GO_BACKEND_URL = import.meta.env.VITE_APP_GO_BACKEND_URL;
const PHP_BACKEND_URL = import.meta.env.VITE_APP_BACKEND_URL;

const getToken = (): Promise<string> => {
    if (!embeddedAppUtils.isAppEmbedded()) {
        return Promise.resolve(storageService.getObject(Locals.ACCESS_TOKEN));
    }
    return shopify.idToken();
};

const buildUrl = (base: string, path: string): string => {
    if (/^https?:\/\//.test(path)) return path;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const parseBody = (text: string): unknown => {
    try {
        return JSON.parse(text);
    } catch (_) {
        return text;
    }
};

export const installConsoleFetcher = (): void => {
    if (typeof window === 'undefined' || !isDebugModeEnabled()) return;

    let base = GO_BACKEND_URL;

    const call = async (
        method: string,
        path: string,
        body?: object,
        options: ConsoleFetcherOptions = {}
    ): Promise<unknown> => {
        const token = await getToken();
        const url = buildUrl(options.base ?? base, path);

        const response = await fetch(url, {
            method,
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
                ...(body !== undefined && { 'Content-Type': 'application/json' }),
                ...options.headers,
            },
            ...(body !== undefined && { body: JSON.stringify(body) }),
        });

        const data = parseBody(await response.text());

        console.log(
            `%c${method} ${response.status}%c ${url}`,
            `color:${response.ok ? '#008060' : '#d72c0d'};font-weight:bold`,
            '',
            data
        );

        return options.raw ? response : data;
    };

    window.pb = {
        get: (path, options) => call('GET', path, undefined, options) as Promise<never>,
        post: (path, body, options) => call('POST', path, body, options) as Promise<never>,
        put: (path, body, options) => call('PUT', path, body, options) as Promise<never>,
        patch: (path, body, options) => call('PATCH', path, body, options) as Promise<never>,
        del: (path, options) => call('DELETE', path, undefined, options) as Promise<never>,
        token: getToken,
        setBase: (next: string) => (base = next),
        get base() {
            return base;
        },
        get go() {
            return GO_BACKEND_URL;
        },
        get php() {
            return PHP_BACKEND_URL;
        },
    };

    console.log(
        [
            `%cpb ready%c  base: ${base}`,
            `  await pb.get('/api/v1/ecom')`,
            `  await pb.post('/api/v1/foxpost/parcel', { orderId: '1234567890', packageSize: 1 })`,
        ].join('\n'),
        'color:#008060;font-weight:bold',
        ''
    );
};
